import { useState, useEffect, memo, useCallback } from "react";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import { CSVLink } from "react-csv";
import BotonSOS from "./sos/boton_sos";
import { obtenerInmueble, obtenerClientes } from "../../functions/consultas";
import { crearRegistro, actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import { CONFIG } from "../../functions/variables_globales";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

const columnas = [
  { label: "Cliente", key: "cliente" },
  { label: "Código", key: "codigo" },
  { label: "Dirección", key: "direccion" },
  { label: "Permiso", key: "permiso" },
  { label: "Estado", key: "estado" }
];

const ROLES_OPCIONES = ["ADMINISTRADOR", "SUPER", "RESIDENTE", "AUDITOR", "GUARDA", "CONTADOR"];

const esAdministrador = () => {
  const permisos = localStorage.getItem("Permisos") || "";
  return ["ADMINISTRADOR", "SUPER"].includes(permisos);
};

const selectorPermisos = (valorActual = "") => `
  <select id="permiso" class="swal2-input" style="cursor:pointer;">
    <option value="">-- Seleccionar --</option>
    ${ROLES_OPCIONES.map(r => `<option value="${r}" ${r === valorActual ? "selected" : ""}>${r}</option>`).join("")}
  </select>
`;

const esCoordinar = () =>
  (localStorage.getItem("CorreoElectronico") || "").toLowerCase().endsWith("@coordinarseguridad.com");

const ListadoInmuebles = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [clientes, setClientes] = useState([]);

  const capturarDatos = async () => {
    try {
      const data = await obtenerInmueble();
      if (Array.isArray(data)) {
        setDatos(data);
      } else {
        const conId = Object.entries(data || {}).map(([id, val]) => ({ ...val, _id: id }));
        setDatos(conId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    capturarDatos();
    if (esCoordinar()) {
      obtenerClientes().then(data => {
        const lista = Object.values(data || {})
          .filter(c => c.RazonSocial)
          .map(c => ({ nit: c.codigo, nombre: c.RazonSocial }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setClientes(lista);
      });
    }
  }, []);

  const formularioHtml = (inmueble = {}, listaClientes = []) => {
    const nitInicial = listaClientes.find(c => c.nombre === inmueble.cliente)?.nit || inmueble.codigo || "";
    const campoCliente = esCoordinar() && listaClientes.length
      ? `<input id="cliente" class="swal2-input" list="lista-clientes-swal"
           placeholder="Escribe para buscar..." value="${inmueble.cliente || ""}" autocomplete="off"
           oninput="
             const match = Array.from(document.querySelectorAll('#lista-clientes-swal option')).find(o => o.value === this.value);
             document.getElementById('nit-display').value = match ? match.dataset.nit : '';
           ">
         <datalist id="lista-clientes-swal">
           ${listaClientes.map(c => `<option value="${c.nombre}" data-nit="${c.nit}">`).join("")}
         </datalist>`
      : `<input id="cliente" class="swal2-input" placeholder="Nombre del cliente o propietario" value="${inmueble.cliente || ""}">`;

    return `
      <style>
        #swal-form .swal2-input, #swal-form select.swal2-input {
          margin: 0 !important; width: 100% !important; box-sizing: border-box !important;
          padding: 10px !important; border: 1px solid #d1d5db !important;
          border-radius: 5px !important; font-size: 14px !important;
        }
      </style>
      <div id="swal-form" style="text-align:left;padding:20px;">
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Cliente / Copropiedad</label>
          ${campoCliente}
        </div>
        ${esCoordinar() ? `
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">NIT</label>
          <input id="nit-display" id="codigo" class="swal2-input" placeholder="Se llena automáticamente" value="${nitInicial}" readonly
            style="background:#f9fafb;color:#6b7280;cursor:default;">
        </div>` : `
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Código del Inmueble</label>
          <input id="codigo" class="swal2-input" placeholder="Ej: 101, APTO-202" value="${inmueble.codigo || ""}">
        </div>`}
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Dirección</label>
          <input id="direccion" class="swal2-input" placeholder="Dirección del inmueble" value="${inmueble.direccion || ""}">
        </div>
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Permiso</label>
          ${selectorPermisos(inmueble.permiso)}
        </div>
        <div style="margin-bottom:15px;">
          <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">Estado</label>
          <select id="estado" class="swal2-input" style="cursor:pointer;">
            <option value="Activo" ${(inmueble.estado || "Activo") === "Activo" ? "selected" : ""}>Activo</option>
            <option value="Inactivo" ${inmueble.estado === "Inactivo" ? "selected" : ""}>Inactivo</option>
          </select>
        </div>
      </div>
    `;
  };

  const leerFormulario = () => {
    const nit      = document.getElementById("nit-display")?.value.trim() || "";
    const codigo   = esCoordinar() ? nit : document.getElementById("codigo").value.trim();
    const direccion = document.getElementById("direccion").value.trim();
    const cliente  = document.getElementById("cliente").value.trim();
    const permiso  = document.getElementById("permiso").value;
    const estado   = document.getElementById("estado").value;

    if (!codigo)   { Swal.showValidationMessage("El código / NIT es obligatorio"); return false; }
    if (!direccion){ Swal.showValidationMessage("La dirección es obligatoria"); return false; }
    if (!cliente)  { Swal.showValidationMessage("El cliente es obligatorio"); return false; }

    return { codigo, direccion, cliente, permiso, estado, nit };
  };

  const agregarInmueble = useCallback(async () => {
    let listaClientes = clientes;
    if (esCoordinar() && !listaClientes.length) {
      const data = await obtenerClientes();
      listaClientes = Object.values(data || {})
        .filter(c => c.RazonSocial)
        .map(c => ({ nit: c.codigo, nombre: c.RazonSocial }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
      setClientes(listaClientes);
    }

    const { value: formValues } = await Swal.fire({
      title: "Agregar Inmueble",
      html: formularioHtml({}, listaClientes),
      showCancelButton: true,
      confirmButtonText: "Agregar",
      cancelButtonText: "Cancelar",
      width: 550,
      preConfirm: leerFormulario
    });

    if (!formValues) return;

    try {
      await crearRegistro(`/usuarios/${CONFIG.correoLimpio()}/inmueble`, {
        codigo: formValues.codigo,
        direccion: formValues.direccion,
        cliente: formValues.cliente,
        permiso: formValues.permiso,
        estado: formValues.estado,
        ...(formValues.nit && { CodigoCliente: formValues.nit }),
      });
      await Swal.fire("Inmueble agregado", "El inmueble fue registrado correctamente.", "success");
      capturarDatos();
    } catch {
      Swal.fire("Error", "No se pudo agregar el inmueble.", "error");
    }
  }, []);

  const editarInmueble = useCallback(async (fila) => {
    let listaClientes = clientes;
    if (esCoordinar() && !listaClientes.length) {
      const data = await obtenerClientes();
      listaClientes = Object.values(data || {})
        .filter(c => c.RazonSocial)
        .map(c => ({ nit: c.codigo, nombre: c.RazonSocial }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
      setClientes(listaClientes);
    }

    const { value: formValues } = await Swal.fire({
      title: "Editar Inmueble",
      html: formularioHtml(fila, listaClientes),
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      width: 550,
      preConfirm: leerFormulario
    });

    if (!formValues) return;

    try {
      await actualizarRegistro(`/usuarios/${CONFIG.correoLimpio()}/inmueble/${fila._id}`, {
        codigo: formValues.codigo,
        direccion: formValues.direccion,
        cliente: formValues.cliente,
        permiso: formValues.permiso,
        estado: formValues.estado,
        ...(formValues.nit && { CodigoCliente: formValues.nit }),
      });
      await Swal.fire("Inmueble actualizado", "Los cambios fueron guardados.", "success");
      capturarDatos();
    } catch {
      Swal.fire("Error", "No se pudo actualizar el inmueble.", "error");
    }
  }, []);

  const confirmarEliminar = useCallback(async (fila) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar Inmueble?",
      html: `
        <div style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #e74c3c;">
            <strong style="display:block;margin-bottom:8px;color:#2c3e50;">${fila.codigo} — ${fila.direccion}</strong>
            <p style="color:#7f8c8d;margin:5px 0;font-size:14px;">Cliente: ${fila.cliente}</p>
            <p style="color:#7f8c8d;margin:0;font-size:14px;">Permiso: ${fila.permiso}</p>
          </div>
          <p style="margin-top:15px;color:#e74c3c;">Esta acción no se puede deshacer.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar"
    });

    if (!isConfirmed) return;

    try {
      await eliminarRegistro(`/usuarios/${CONFIG.correoLimpio()}/inmueble/${fila._id}`);
      Swal.fire({ title: "Eliminado", text: "Inmueble eliminado correctamente.", icon: "success", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire("Error", "No se pudo eliminar el inmueble.", "error");
    }
  }, []);

  const botonesAccion = [
    { texto: "Acceder", icono: "bi-box-arrow-in-right", accion: (fila) => {
        sessionStorage.setItem("InmuebleSeleccionado", JSON.stringify({
          permiso: fila.permiso,
          codigo: fila.codigo,
          direccion: fila.direccion,
          razonSocial: fila.cliente
        }));

        window.location.replace("/inicio");
      }
    },
    ...(esAdministrador() ? [
      { texto: "Editar", icono: "bi-pencil", accion: (fila) => editarInmueble(fila) },
      { texto: "Eliminar", icono: "bi-trash", accion: (fila) => confirmarEliminar(fila) }
    ] : [])
  ];

  return cargando ? (
    <AnimacionCarga />
  ) : (
    <>
      <TablaGenerica
        datos={datos}
        titulos={columnas.map(c => c.label.toUpperCase())}
        campos={columnas.map(c => c.key)}
        elementosPorPagina={500}
        botones={botonesAccion}
        contenidoExtra={
          <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <CSVLink data={datos} headers={columnas} filename="listado_inmuebles.csv" separator=";" style={{ padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" }} title="Exportar"><i className="bi bi-download" /></CSVLink>
            {esAdministrador() && (
              <button onClick={agregarInmueble} title="Agregar Inmueble"><i className="bi bi-plus-lg" /></button>
            )}
          </div>
        }
      />

      <BotonSOS />
    </>
  );
});

export default ListadoInmuebles;
